<script setup lang="ts">
// Orchestrator: renders the trigger button (badge icon) and the fullscreen
// dialog shell, owns the hover/selection state shared between the graph and
// the list, and drives the fly-out choreography via useZoomFromIcon.
import { mainNavigationQaEquipment } from '@/components/icons/icons';
import Button from '@/components/buttons/Button.vue';
import { computed, ref, toRef } from 'vue';
import DeactivatedChamberModalGraph from './DeactivatedChamberModalGraph.vue';
import DeactivatedChamberModalList from './DeactivatedChamberModalList.vue';
import type { DeactivatedChamber } from './types';
import { useDetectorLayout } from './useDetectorLayout';
import { useZoomFromIcon } from './useZoomFromIcon';

// Multi-root template (trigger + dialog) disables automatic attr fallthrough;
// forward attrs (e.g. layout classes from the parent) to the trigger button.
defineOptions({ inheritAttrs: false });

// The layout is fetched from the backend by device type number (e.g. "T10043");
// the deactivated chamber ids come pre-parsed from the calibration's
// deactivatedChambersDC/UC fields.
type DeactivatedChamberModalProps = {
  typeNumber?: string;
  model?: string;
  deactivatedChambersDc?: number[];
  deactivatedChambersUc?: number[];
};
const props = withDefaults(defineProps<DeactivatedChamberModalProps>(), {
  typeNumber: undefined,
  model: undefined,
  deactivatedChambersDc: () => [],
  deactivatedChambersUc: () => [],
});

const { pad, size, dots, dotIds, frames, hasLayout } = useDetectorLayout(
  toRef(props, 'typeNumber'),
);

// DC = deactivated by PTW, UC = deactivated by the user; ids arriving in both
// lists count once, as DC.
const deactivatedChambers = computed<DeactivatedChamber[]>(() => {
  const byId = new Map<number, DeactivatedChamber>();
  for (const id of props.deactivatedChambersDc) byId.set(id, { id, group: 'DC' });
  for (const id of props.deactivatedChambersUc) {
    if (!byId.has(id)) byId.set(id, { id, group: 'UC' });
  }
  return [...byId.values()].sort((a, b) => a.id - b.id);
});

// Hover/selection state shared between the graph and the list
const hoveredId = ref<number>();
const selectedChamberId = ref<number>();
const showFrameLabels = ref(false);

const graph = ref<InstanceType<typeof DeactivatedChamberModalGraph>>();
const {
  isOpen,
  growOffset,
  isZoomReady,
  isRevealed,
  isClosing,
  openFromIcon,
  onAnimationEnd,
  requestClose,
  onDialogModelUpdate,
} = useZoomFromIcon(computed(() => graph.value?.svgEl));

function openDialog(event: MouseEvent) {
  // fresh view state each open; the previous hover/selection/zoom would
  // otherwise point at the last session
  hoveredId.value = undefined;
  selectedChamberId.value = undefined;
  showFrameLabels.value = false;
  openFromIcon(event);
}
</script>

<template>
  <Button
    v-if="deactivatedChambers.length"
    class="chambers-trigger"
    v-bind="$attrs"
    variant="text"
    density="comfortable"
    roundIcon
    :disabled="!hasLayout"
    :aria-label="$t('ShowDeactivatedChambers')"
    @click.stop="openDialog"
  >
    <v-icon size="36" :icon="mainNavigationQaEquipment" />
    <span class="trigger-badge">{{ deactivatedChambers.length }}</span>
  </Button>

  <v-dialog
    :modelValue="isOpen"
    fullscreen
    :scrim="false"
    transition="fade-transition"
    @update:modelValue="onDialogModelUpdate"
  >
    <v-card
      class="chambers-card"
      :class="{ revealed: isZoomReady && !isClosing }"
      :style="{
        '--grow-tx': `${growOffset.x}px`,
        '--grow-ty': `${growOffset.y}px`,
        '--grow-scale': `${growOffset.scale}`,
      }"
    >
      <Button
        class="close-btn"
        variant="text"
        icon="$close"
        :aria-label="$t('Close')"
        @click="requestClose"
      />

      <div class="chambers-body">
        <DeactivatedChamberModalGraph
          ref="graph"
          v-model:hoveredId="hoveredId"
          v-model:selectedChamberId="selectedChamberId"
          :size="size"
          :pad="pad"
          :dots="dots"
          :frames="frames"
          :deactivatedChambers="deactivatedChambers"
          :showFrameLabels="showFrameLabels"
          :zoomReady="isZoomReady && !isClosing"
          :closing="isClosing"
          @animationEnd="onAnimationEnd"
        />

        <DeactivatedChamberModalList
          v-if="isRevealed && !isClosing"
          v-model:hoveredId="hoveredId"
          v-model:selectedChamberId="selectedChamberId"
          v-model:showFrameLabels="showFrameLabels"
          :chambers="deactivatedChambers"
          :layoutChamberIds="dotIds"
          :totalChambers="dots.length"
          :model="model"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.chambers-trigger {
  position: relative;

  .trigger-badge {
    // centered on the icon's top-right corner (the icon sits centered in the
    // padded button, so anchor from the button's center, not its edge)
    // half the visible glyph, not the 36px icon box — the svg has internal
    // padding, so the drawn detector's corner sits further in than the box edge
    --icon-half: 14px;
    --badge-half: 8px; // half the 16px badge
    position: absolute;
    top: calc(50% - var(--icon-half) - var(--badge-half));
    right: calc(50% - var(--icon-half) - var(--badge-half));
    min-width: 16px;
    height: 16px;
    border-radius: 8px;
    background: rgb(var(--v-theme-error));
    color: white;
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
  }

  &:hover .v-icon {
    transform: scale(1.05);
  }

  .v-icon {
    transition: transform 0.2s ease;
  }
}

.chambers-card {
  overflow: hidden;
  position: relative;
  z-index: 0;
  background: transparent;
  color: #fff;
  box-shadow: none;

  @media (max-width: 1024px) {
    height: 100dvh;
    overflow: hidden auto;
  }

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: -1;
    background: rgba(var(--v-theme-primary-darken-2), 0.75);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    opacity: 0;
    // fades in over the same span as the detector's scale animation
    transition: opacity 0.9s ease;
    pointer-events: none;
  }

  &.revealed::before {
    opacity: 1;
  }
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
}

.chambers-body {
  display: flex;
  gap: 40px;
  padding: 24px;
  align-items: center;
  height: 100dvh;

  @media (max-width: 1024px) {
    flex-direction: column;
    height: auto;
    // stretch so both the graph and the list span the full width
    align-items: stretch;
  }
}
</style>
